import React, { useState } from 'react';
import { PhoneIcon, MessageCircleIcon, MailIcon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { copy } from '../data/copy';
import { business, contacts } from '../data/business';
import type { Localized } from '../types';
const serviceOptions: {
  value: string;
  label: Localized;
}[] = [{
  value: 'deep',
  label: {
    en: 'Deep Clean',
    es: 'Limpieza Profunda'
  }
}, {
  value: 'regular',
  label: {
    en: 'Regular Cleaning',
    es: 'Limpieza Regular'
  }
}, {
  value: 'bathroom',
  label: {
    en: 'Bathroom',
    es: 'Baño'
  }
}, {
  value: 'kitchen',
  label: {
    en: 'Kitchen',
    es: 'Cocina'
  }
}, {
  value: 'move',
  label: {
    en: 'Move-in / Move-out',
    es: 'Mudanza de Entrada / Salida'
  }
}, {
  value: 'other',
  label: {
    en: 'Other',
    es: 'Otro'
  }
}];
const sizeOptions: {
  value: string;
  label: Localized;
}[] = [{
  value: '1-2',
  label: {
    en: '1–2 bedrooms',
    es: '1–2 recámaras'
  }
}, {
  value: '3-4',
  label: {
    en: '3–4 bedrooms',
    es: '3–4 recámaras'
  }
}, {
  value: '5+',
  label: {
    en: '5+ bedrooms',
    es: '5+ recámaras'
  }
}, {
  value: 'commercial',
  label: {
    en: 'Commercial',
    es: 'Comercial'
  }
}];
interface Errors {
  name?: string;
  phone?: string;
  service?: string;
}
const fieldClass = 'mt-1.5 w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-charcoal outline-none transition-colors duration-150 ease-out focus:border-bright';
const labelClass = 'font-display text-sm font-bold text-navy';
export function BookingForm() {
  const {
    t
  } = useLanguage();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const [values, setValues] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    size: '',
    date: '',
    time: '',
    requests: '',
    subscribe: false
  });
  const update = (field: keyof typeof values, value: string | boolean) => {
    setValues(current => ({
      ...current,
      [field]: value
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Errors = {};
    if (values.name.trim().length < 2) nextErrors.name = t(copy.formNameError);
    if (values.phone.replace(/\D/g, '').length < 10) nextErrors.phone = t(copy.formPhoneError);
    if (!values.service) nextErrors.service = t(copy.formServiceError);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 700);
  };
  const reset = () => {
    setValues({
      name: '',
      phone: '',
      email: '',
      service: '',
      size: '',
      date: '',
      time: '',
      requests: '',
      subscribe: false
    });
    setStatus('idle');
  };
  return <section id="contact" className="bg-navy" data-id="element-46" data-mp="vt92jjito0k8">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-id="element-47" data-mp="vqeiht3q0hrc">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.25fr] lg:gap-16" data-id="element-48" data-mp="10qj1pqw0jql9">
          <div data-id="element-49" data-mp="2laedh3k9ipi6">
            <p className="font-display text-sm font-bold text-aqua" data-id="element-50" data-mp="2l9utlhb1xr1i">{t(copy.formKicker)}</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-51" data-mp="2gbl2piwd1clp">
              {t(copy.formTitle)}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/75" data-id="element-52" data-mp="2q8647u9685ks">{t(copy.formSubtitle)}</p>

            <div className="mt-10" data-id="element-53" data-mp="33z1jboa9jiwt">
              <p className="font-display text-xs font-bold uppercase tracking-[0.14em] text-white/50" data-id="element-54" data-mp="33xzu8fwewvw3">
                {t(copy.orReachUs)}
              </p>
              <ul className="mt-4 space-y-5" data-id="element-55" data-mp="3dyjwnlvo46fw">
                {contacts.map(contact => <li key={contact.id} data-id="element-56" data-mp="38ye5e6tb8lyc">
                    <a href={contact.phoneHref} className="flex items-center gap-3 font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:text-aqua" data-id="element-57" data-mp="38zgss4rfc69i">
                      <PhoneIcon className="h-5 w-5 shrink-0 text-aqua" aria-hidden="true" data-id="element-58" data-mp="5o7wp8fxxssb" />
                      {t(copy.callPerson)} {contact.name}: {contact.phone}
                    </a>
                    <a href={contact.whatsappHref} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-3 text-white/85 transition-colors duration-150 ease-out hover:text-aqua" data-id="element-59" data-mp="fm5izqe8pfia">
                      <MessageCircleIcon className="h-5 w-5 shrink-0 text-aqua" aria-hidden="true" data-id="element-60" data-mp="3o1paajvconup" />
                      WhatsApp: {contact.phone}
                    </a>
                  </li>)}
                <li data-id="element-61" data-mp="pegv9d6lpoz">
                  <a href={business.emailHref} className="flex items-center gap-3 break-all text-white/85 transition-colors duration-150 ease-out hover:text-aqua" data-id="element-62" data-mp="pobxbd32n1j">
                    <MailIcon className="h-5 w-5 shrink-0 text-aqua" aria-hidden="true" data-id="element-63" data-mp="34c56xoy187u2" />
                    {t(copy.emailLabel)}: {business.email}
                  </a>
                </li>
              </ul>
              <p className="mt-6 inline-flex rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-aqua" data-id="element-64" data-mp="2rt9gfb6ku8qx">
                {t(copy.sameDay)}
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-white p-6 shadow-lift sm:p-8" data-id="element-65" data-mp="2iws444mh84tl">
            {status === 'success' ? <div className="flex h-full flex-col items-start justify-center py-8" data-id="element-66" data-mp="294us4vnn98tt">
                <CheckCircle2Icon className="h-10 w-10 text-bright" aria-hidden="true" data-id="element-67" data-mp="292rupqk5wdf2" />
                <h3 className="mt-4 font-display text-xl font-extrabold text-navy" data-id="element-68" data-mp="2461ft6k3fnv8">
                  {t(copy.formSuccessTitle)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-charcoal/70" data-id="element-69" data-mp="1v5ms60jnpwok">
                  {t(copy.formSuccessBody)}
                </p>
                <button type="button" onClick={reset} className="mt-6 rounded-xl border border-royal px-5 py-3 font-display text-sm font-bold text-royal transition-colors duration-150 ease-out hover:bg-royal hover:text-white" data-id="element-70" data-mp="1q6s86v9ye22v">
                  {t(copy.formSuccessAgain)}
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate className="grid gap-5 sm:grid-cols-2" data-id="element-71" data-mp="1bjjp78aeksgc">
                {Object.keys(errors).length > 0 ? <p role="alert" className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700 sm:col-span-2" data-id="element-72" data-mp="248if4rkroyw7">
                    <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-73" data-mp="1uhe0jsz07g1w" />
                    {t(copy.formErrorSummary)}
                  </p> : null}

                <div className="sm:col-span-2" data-id="element-74" data-mp="2my6uvdg1s8qz">
                  <label className={labelClass} htmlFor="booking-name" data-id="element-75" data-mp="2n01gktz584w4">
                    {t(copy.formName)} *
                  </label>
                  <input id="booking-name" type="text" required value={values.name} onChange={event => update('name', event.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'booking-name-error' : undefined} className={fieldClass} data-id="element-76" data-mp="6keliojqcl13" />
                  {errors.name ? <p id="booking-name-error" className="mt-1.5 text-xs font-medium text-red-600" data-id="element-77" data-mp="3jt9g596g1s0o">
                      {errors.name}
                    </p> : null}
                </div>

                <div data-id="element-78" data-mp="1d0gswztonrpx">
                  <label className={labelClass} htmlFor="booking-phone" data-id="element-79" data-mp="1czxj66e6s9mz">
                    {t(copy.formPhone)} *
                  </label>
                  <input id="booking-phone" type="tel" required value={values.phone} onChange={event => update('phone', event.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'booking-phone-error' : undefined} className={fieldClass} data-id="element-80" data-mp="18yc3yee6jj9m" />
                  {errors.phone ? <p id="booking-phone-error" className="mt-1.5 text-xs font-medium text-red-600" data-id="element-81" data-mp="3l7alxcdvm2lt">
                      {errors.phone}
                    </p> : null}
                </div>

                <div data-id="element-82" data-mp="3v6t4boe51gbr">
                  <label className={labelClass} htmlFor="booking-email" data-id="element-83" data-mp="3v4f83c7q1y2m">
                    {t(copy.formEmail)}
                  </label>
                  <input id="booking-email" type="email" value={values.email} onChange={event => update('email', event.target.value)} className={fieldClass} data-id="element-84" data-mp="20t0h6s2z5sla" />
                </div>

                <div data-id="element-85" data-mp="i58nxuynyh1i">
                  <label className={labelClass} htmlFor="booking-service" data-id="element-86" data-mp="i4p9io882as8">
                    {t(copy.formService)} *
                  </label>
                  <select id="booking-service" required value={values.service} onChange={event => update('service', event.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'booking-service-error' : undefined} className={fieldClass} data-id="element-87" data-mp="94sqp38ik653">
                    <option value="" data-id="element-88" data-mp="ru8qfocdjukg">{t(copy.formSelect)}</option>
                    {serviceOptions.map(option => <option key={option.value} value={option.value} data-id="element-89" data-mp="3hm70z5hc4wiy">
                        {t(option.label)}
                      </option>)}
                  </select>
                  {errors.service ? <p id="booking-service-error" className="mt-1.5 text-xs font-medium text-red-600" data-id="element-90" data-mp="29kw4it0i6r2o">
                      {errors.service}
                    </p> : null}
                </div>

                <div data-id="element-91" data-mp="2el0q2xg9qbzr">
                  <label className={labelClass} htmlFor="booking-size" data-id="element-92" data-mp="2sdqc31vsrgwv">
                    {t(copy.formHomeSize)}
                  </label>
                  <select id="booking-size" value={values.size} onChange={event => update('size', event.target.value)} className={fieldClass} data-id="element-93" data-mp="2ndluf8j5kg52">
                    <option value="" data-id="element-94" data-mp="2xaqlnmruv1sq">{t(copy.formSelect)}</option>
                    {sizeOptions.map(option => <option key={option.value} value={option.value} data-id="element-95" data-mp="12ysbnilq21vs">
                        {t(option.label)}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-96" data-mp="3jlqigq6kr84y">
                  <label className={labelClass} htmlFor="booking-date" data-id="element-97" data-mp="3jk71exd3gt24">
                    {t(copy.formDate)}
                  </label>
                  <input id="booking-date" type="date" value={values.date} onChange={event => update('date', event.target.value)} className={fieldClass} data-id="element-98" data-mp="34u84sz16tpor" />
                </div>

                <div data-id="element-99" data-mp="2zv4y7a5k7beo">
                  <label className={labelClass} htmlFor="booking-time" data-id="element-100" data-mp="2lxggucapamed">
                    {t(copy.formTime)}
                  </label>
                  <input id="booking-time" type="time" value={values.time} onChange={event => update('time', event.target.value)} className={fieldClass} data-id="element-101" data-mp="2qxm8hsgz08wg" />
                </div>

                <div className="sm:col-span-2" data-id="element-102" data-mp="np2ek1lmhirg">
                  <label className={labelClass} htmlFor="booking-requests" data-id="element-103" data-mp="nlodcwop3fll">
                    {t(copy.formRequests)}
                  </label>
                  <textarea id="booking-requests" rows={4} value={values.requests} onChange={event => update('requests', event.target.value)} placeholder={t(copy.formRequestsPlaceholder)} className={`${fieldClass} resize-none`} data-id="element-104" data-mp="dp1w0hvkv6ob" />
                </div>

                <label className="flex items-center gap-3 text-sm text-charcoal/80 sm:col-span-2" data-id="element-105" data-mp="wnxmpj8892xe">
                  <input type="checkbox" checked={values.subscribe} onChange={event => update('subscribe', event.target.checked)} className="h-4 w-4 rounded border-navy/25 text-bright focus:ring-bright" data-id="element-106" data-mp="rqe98ljcsu6i" />
                  {t(copy.formSubscribe)}
                </label>

                <button type="submit" disabled={status === 'submitting'} className="rounded-xl bg-bright px-6 py-4 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-royal disabled:opacity-70 sm:col-span-2" data-id="element-107" data-mp="1woj9wi32sqro">
                  {status === 'submitting' ? t(copy.formSubmitting) : t(copy.formSubmit)}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}